import React, { useCallback, useState, useRef } from 'react'
import Link from '@/components/link-component'
import ImageContentful from './image-contentful'
import ImageZoomDialog from './image-zoom-dialog'
import { TypeResourceImage } from '@/types/ctf/TypeResourceImage'
import { cx } from '@/utils/strings'
import { useInspectorProps } from '@/utils/live-preview'

export type ImageProps = TypeResourceImage<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  entryId?: string
  className?: string
  loading?: any
}

export default function Image({
  entryId,
  className,
  loading,
  image,
  imageDarkMode,
  link,
  showCaption,
  caption,
  captionSize,
  noOptimization,
  zoomable,
  imageShadow = false,
  roundedCorners = false,
}: ImageProps) {
  const pageInspectorProps = useInspectorProps({ entryId, restrictedTypes: ['pages'] })

  const fields = image?.fields || {}
  const [isZoomed, setIsZoomed] = useState(false)

  const triggerRef = useRef<HTMLDivElement>(null)

  const handleZoomToggle = useCallback(() => {
    if (window.innerWidth > 1024) {
      setIsZoomed(true)
    }
  }, [])

  const handleZoomClose = useCallback(() => {
    setIsZoomed(false)
  }, [])

  const Wrapper = useCallback(
    ({ children }: { children: React.ReactNode }) => {
      if (link?.fields?.url) return <Link to={link.fields.url}>{children}</Link>
      return children
    },
    [link?.fields?.url]
  )

  const width = fields.file?.details?.image?.width
  const height = fields.file?.details?.image?.height
  const title = caption || image?.fields?.title

  return (
    <>
      <Wrapper>
        <figure
          className="mb-10 print:[break-inside:avoid]"
          style={{
            maxWidth: width ? `calc(min(${width}px, 100%))` : '100%',
          }}
          {...pageInspectorProps({ fieldId: 'image' })}
        >
          <div
            ref={triggerRef}
            className={cx(zoomable && 'lg:cursor-zoom-in')}
            onClick={zoomable ? handleZoomToggle : undefined}
            style={{
              aspectRatio: width && height ? `${width} / ${height}` : undefined,
            }}
          >
            <ImageContentful
              {...fields}
              darkModeImage={imageDarkMode?.fields}
              className={className}
              loading={loading}
              noOptimization={noOptimization}
              imageShadow={imageShadow}
              roundedCorners={roundedCorners}
            />
          </div>
          {showCaption && !isZoomed && (
            <figcaption
              className={cx(
                'w-fit mx-auto text-center mt-2 print:mt-1 max-w-sm',
                (!captionSize || captionSize === 'Small') && 'text-xs text-secondary-600 dark:text-secondary-300',
                captionSize === 'Large' && 'text-lg text-black dark:text-white'
              )}
            >
              <cite>{title}</cite>
              {title && image?.fields?.description && <> &#8212; </>}
              {image?.fields?.description}
            </figcaption>
          )}
        </figure>
      </Wrapper>

      {zoomable && (
        <ImageZoomDialog
          isOpen={isZoomed}
          onClose={handleZoomClose}
          image={image?.fields}
          darkModeImage={imageDarkMode?.fields}
          noOptimization={noOptimization}
          showCaption={showCaption}
          caption={caption}
          triggerElement={triggerRef.current}
          imageShadow={imageShadow}
          roundedCorners={roundedCorners}
        />
      )}
    </>
  )
}
